import React, { useState, useRef, useEffect } from 'react';

const DraggablePane = ({ 
  children, 
  id, 
  initialPosition = { x: 0, y: 0 }, 
  initialSize = { width: 400, height: 600 },
  minSize = { width: 300, height: 400 },
  onFocus,
  onPositionChange,
  onSizeChange,
  isFocused = false,
  zIndex = 1
}) => {
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  const paneRef = useRef(null);
  const resizeHandleRef = useRef(null);

  // Update position and size when props change
  useEffect(() => {
    setPosition(initialPosition);
  }, [initialPosition]);

  useEffect(() => {
    setSize(initialSize);
  }, [initialSize]);

  // Handle mouse down on pane header for dragging
  const handleMouseDown = (e) => {
    // Only allow dragging from the header area
    if (!e.target.closest('.aim-chat-header') && !e.target.closest('.resize-handle')) {
      return;
    }
    
    if (e.target.closest('.resize-handle')) return; // Don't start drag if clicking resize handle
    
    setIsDragging(true);
    const rect = paneRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    
    // Bring to front
    if (onFocus) {
      onFocus(id);
    }
  };

  // Handle mouse down on resize handle
  const handleResizeMouseDown = (e) => {
    e.stopPropagation();
    setIsResizing(true);
    const rect = paneRef.current.getBoundingClientRect();
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: rect.width,
      height: rect.height
    });
    
    // Bring to front
    if (onFocus) {
      onFocus(id);
    }
  };

  // Handle mouse move for dragging and resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Keep pane within viewport bounds
        const maxX = window.innerWidth - size.width;
        const maxY = window.innerHeight - size.height;
        
        const newPosition = {
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        };
        
        setPosition(newPosition);
        
        // Notify parent of position change
        if (onPositionChange) {
          onPositionChange(id, newPosition);
        }
      }
      
      if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        
        const newWidth = Math.max(minSize.width, resizeStart.width + deltaX);
        const newHeight = Math.max(minSize.height, resizeStart.height + deltaY);
        
        // Keep pane within viewport bounds
        const maxWidth = window.innerWidth - position.x;
        const maxHeight = window.innerHeight - position.y;
        
        const newSize = {
          width: Math.min(newWidth, maxWidth),
          height: Math.min(newHeight, maxHeight)
        };
        
        setSize(newSize);
        
        // Notify parent of size change
        if (onSizeChange) {
          onSizeChange(id, newSize);
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragOffset, resizeStart, size, position, minSize, onPositionChange, onSizeChange, id]);

  return (
    <div
      ref={paneRef}
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: isFocused ? 1000 : zIndex,
        cursor: isDragging ? 'grabbing' : 'default',
        userSelect: 'none'
      }}
      onMouseDown={handleMouseDown}
    >
      {children}
      <div
        ref={resizeHandleRef}
        className="resize-handle"
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '20px',
          height: '20px',
          cursor: 'nw-resize',
          background: 'linear-gradient(-45deg, transparent 30%, #808080 30%, #808080 70%, transparent 70%)',
          zIndex: 1
        }}
        onMouseDown={handleResizeMouseDown}
      />
    </div>
  );
};

export default DraggablePane; 